import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Loader2, Eye, EyeOff, Megaphone, Package,
  CheckCircle2, XCircle, ChevronUp, ChevronDown,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SHOW_OPTIONS = [
  { value: 'all',       label: 'All categories' },
  { value: 'published', label: 'Published only' },
  { value: 'hidden',    label: 'Hidden only' },
];

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

export default function OGCategoryPublishing() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, hidden: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [toggling, setToggling] = useState(null); // category name being toggled
  const [editingSort, setEditingSort] = useState(null);
  const [editingSortValue, setEditingSortValue] = useState('');
  const [editingIcon, setEditingIcon] = useState(null);
  const [editingIconValue, setEditingIconValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `?show_filter=${filter}`;
      const res = await axios.get(API + '/admin/og-categories' + qs, { headers: authHeaders() });
      setItems(res.data.items || []);
      setStats({ total: res.data.total, published: res.data.published, hidden: res.data.hidden });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load OG categories');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const t = search.toLowerCase();
    return items.filter(c => (c.name || '').toLowerCase().includes(t));
  }, [items, search]);

  const allSelected = filtered.length > 0 && filtered.every(c => selected.has(c.name));

  const toggleOne = async (cat) => {
    setToggling(cat.name);
    try {
      const res = await axios.patch(
        `${API}/admin/categories/${encodeURIComponent(cat.name)}`,
        { show_on_og_app: !cat.show_on_og_app },
        { headers: authHeaders() },
      );
      setItems(prev => prev.map(c => c.name === cat.name
        ? { ...c, ...res.data.category } : c));
      setStats(s => ({
        total: s.total,
        published: s.published + (!cat.show_on_og_app ? 1 : -1),
        hidden: s.hidden + (cat.show_on_og_app ? 1 : -1),
      }));
      toast.success(`"${cat.name}" ${!cat.show_on_og_app ? 'published' : 'hidden'} on OG App`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Toggle failed');
    } finally {
      setToggling(null);
    }
  };

  const saveSort = async (cat) => {
    const val = parseInt(editingSortValue, 10);
    if (Number.isNaN(val)) { setEditingSort(null); return; }
    if (val === cat.og_sort_order) { setEditingSort(null); return; }
    try {
      const res = await axios.patch(
        `${API}/admin/categories/${encodeURIComponent(cat.name)}`,
        { og_sort_order: val }, { headers: authHeaders() });
      setItems(prev => prev.map(c => c.name === cat.name
        ? { ...c, ...res.data.category } : c));
      toast.success(`Sort order updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sort update failed');
    } finally {
      setEditingSort(null);
    }
  };

  const saveIcon = async (cat) => {
    if (editingIconValue === (cat.og_icon || '')) { setEditingIcon(null); return; }
    try {
      const res = await axios.patch(
        `${API}/admin/categories/${encodeURIComponent(cat.name)}`,
        { og_icon: editingIconValue }, { headers: authHeaders() });
      setItems(prev => prev.map(c => c.name === cat.name
        ? { ...c, ...res.data.category } : c));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Icon update failed');
    } finally {
      setEditingIcon(null);
    }
  };

  const bulkPublish = async (show) => {
    if (selected.size === 0) { toast.error('Select at least one category'); return; }
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/categories/bulk-publish`,
        { category_ids: Array.from(selected), show },
        { headers: authHeaders() });
      toast.success(`${res.data.updated} categor${res.data.updated === 1 ? 'y' : 'ies'} ${show ? 'published' : 'hidden'} on OG`);
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map(c => c.name)));
  };

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="og-category-publishing-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-indigo-600" />
            OG Category Publishing
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Control which product categories show up on the OG Retailer App.
            <span className="font-semibold text-slate-700"> Toggle OFF</span> to hide a category
            and every product inside it — instantly.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="og-cat-stat-total">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Total Categories</p>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card data-testid="og-cat-stat-published">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-600 uppercase tracking-wide font-semibold">Published on OG</p>
            <p className="text-2xl font-bold mt-1 text-emerald-700">{stats.published}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {stats.total > 0
                ? `${stats.published} of ${stats.total} categories visible on OG App`
                : ''}
            </p>
          </CardContent>
        </Card>
        <Card data-testid="og-cat-stat-hidden">
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Hidden</p>
            <p className="text-2xl font-bold mt-1 text-slate-500">{stats.hidden}</p>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search categories..." className="pl-8"
            data-testid="og-cat-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="og-cat-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SHOW_OPTIONS.map(o =>
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected.size > 0 && (
          <>
            <Badge variant="outline" className="text-indigo-700 border-indigo-300">
              {selected.size} selected
            </Badge>
            <Button size="sm" variant="outline"
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              onClick={() => bulkPublish(true)} disabled={bulkBusy}
              data-testid="og-cat-bulk-publish-btn">
              <Eye className="w-4 h-4 mr-1" />Publish {selected.size}
            </Button>
            <Button size="sm" variant="outline"
              className="text-slate-600 border-slate-300 hover:bg-slate-50"
              onClick={() => bulkPublish(false)} disabled={bulkBusy}
              data-testid="og-cat-bulk-hide-btn">
              <EyeOff className="w-4 h-4 mr-1" />Hide {selected.size}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Categories</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500 py-8 text-center">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="og-cat-empty">
              No categories match your filter.
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-700 text-white">
                  <tr>
                    <th className="px-3 py-2.5 w-10">
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                        data-testid="og-cat-select-all" />
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Category</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Products</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Show on OG</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Sort</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Icon</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Last Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(cat => (
                    <tr key={cat.name} className="border-t hover:bg-slate-50"
                      data-testid={`og-cat-row-${cat.name}`}>
                      <td className="px-3 py-2.5">
                        <Checkbox checked={selected.has(cat.name)}
                          onCheckedChange={() => toggleSelect(cat.name)}
                          data-testid={`og-cat-select-${cat.name}`} />
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          {cat.og_icon && <span className="text-lg">{cat.og_icon}</span>}
                          <span className="font-medium">{cat.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="inline-flex items-center gap-1 text-slate-600">
                          <Package className="w-3.5 h-3.5" />
                          <span className="font-mono">{cat.product_count}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <Switch checked={!!cat.show_on_og_app}
                            disabled={toggling === cat.name}
                            onCheckedChange={() => toggleOne(cat)}
                            data-testid={`og-cat-toggle-${cat.name}`} />
                          {cat.show_on_og_app
                            ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                                <CheckCircle2 className="w-3 h-3 mr-1" />Live
                              </Badge>
                            : <Badge variant="outline" className="text-slate-500">
                                <XCircle className="w-3 h-3 mr-1" />Hidden
                              </Badge>}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        {editingSort === cat.name ? (
                          <Input type="number" value={editingSortValue}
                            onChange={(e) => setEditingSortValue(e.target.value)}
                            onBlur={() => saveSort(cat)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveSort(cat); if (e.key === 'Escape') setEditingSort(null); }}
                            autoFocus className="w-20 h-7"
                            data-testid={`og-cat-sort-input-${cat.name}`} />
                        ) : (
                          <button className="font-mono text-slate-700 hover:bg-slate-100 px-2 py-0.5 rounded"
                            onClick={() => { setEditingSort(cat.name); setEditingSortValue(String(cat.og_sort_order || 0)); }}
                            data-testid={`og-cat-sort-${cat.name}`}>
                            {cat.og_sort_order ?? 0}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {editingIcon === cat.name ? (
                          <Input value={editingIconValue}
                            onChange={(e) => setEditingIconValue(e.target.value.slice(0, 4))}
                            onBlur={() => saveIcon(cat)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveIcon(cat); if (e.key === 'Escape') setEditingIcon(null); }}
                            autoFocus className="w-16 h-7 text-center"
                            placeholder="🥣"
                            data-testid={`og-cat-icon-input-${cat.name}`} />
                        ) : (
                          <button className="hover:bg-slate-100 px-2 py-0.5 rounded min-w-[2rem] text-lg"
                            onClick={() => { setEditingIcon(cat.name); setEditingIconValue(cat.og_icon || ''); }}
                            data-testid={`og-cat-icon-${cat.name}`}>
                            {cat.og_icon || <span className="text-xs text-slate-400">+ Add</span>}
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">
                        {fmtDateTime(cat.og_updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 text-center">
        Tip — click a sort number or icon cell to edit inline. Press Enter to save, Esc to cancel.
      </p>
    </div>
  );
}
